import * as React from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtCents } from "@/lib/time";
import { toast } from "sonner";
import { X, Plus, Trash2, Printer, CheckCircle2, XCircle } from "lucide-react";
import DetailDrawer from "@/components/DetailDrawer";

const STATUS_CHIP = {
  draft:  "bg-brand-rule/60 text-brand-muted",
  issued: "bg-amber-100 text-amber-800",
  paid:   "bg-emerald-100 text-emerald-800",
  void:   "bg-coral/15 text-coral",
};

/**
 * InvoiceDrawer — create / view / transition an invoice.
 *
 * Draft invoices are editable; issued/paid/void are read-only with
 * transition buttons only.  Server computes totals in integer pence;
 * client just displays them.
 */
export default function InvoiceDrawer({
  open, mode, invoiceId, companyId, canManage, customers, onClose, onSaved,
}) {
  const isNew = mode === "new";
  const [invoice, setInvoice] = React.useState(null);
  const [form, setForm] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) { setInvoice(null); setForm(null); return; }
    if (isNew) {
      setInvoice(null);
      setForm({
        customer_id: customers[0]?.id || "",
        issue_date: "",
        due_date: "",
        notes: "",
        line_items: [{ description: "", qty: 1, unit_price_cents: 0 }],
      });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/companies/${companyId}/invoices/${invoiceId}`);
        setInvoice(data);
        if (data.status === "draft") {
          setForm({
            customer_id: data.customer_id,
            issue_date: data.issue_date,
            due_date: data.due_date,
            notes: data.notes || "",
            line_items: data.line_items.map((li) => ({
              description: li.description, qty: li.qty, unit_price_cents: li.unit_price_cents,
            })),
          });
        } else {
          setForm(null);
        }
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load invoice");
      }
    })();
  }, [open, isNew, invoiceId, companyId, customers]);

  const linesTotal = form
    ? form.line_items.reduce((s, li) => s + Math.round(Number(li.qty || 0) * Number(li.unit_price_cents || 0)), 0)
    : (invoice?.total_cents ?? 0);

  function setLine(idx, key, val) {
    setForm((f) => ({
      ...f,
      line_items: f.line_items.map((li, i) => i === idx ? { ...li, [key]: val } : li),
    }));
  }
  function addLine() {
    setForm((f) => ({ ...f, line_items: [...f.line_items, { description: "", qty: 1, unit_price_cents: 0 }] }));
  }
  function delLine(idx) {
    setForm((f) => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));
  }

  async function save() {
    setBusy(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        line_items: form.line_items.map((li) => ({
          description: li.description.trim() || "Line item",
          qty: Number(li.qty || 0),
          unit_price_cents: Math.round(Number(li.unit_price_cents || 0)),
        })).filter((li) => li.qty > 0 && li.unit_price_cents >= 0),
        issue_date: form.issue_date || undefined,
        due_date: form.due_date || undefined,
        notes: form.notes.trim() || null,
      };
      if (payload.line_items.length === 0) {
        toast.error("Need at least one line item with qty > 0");
        setBusy(false); return;
      }
      if (isNew) {
        await api.post(`/companies/${companyId}/invoices`, payload);
      } else {
        const { customer_id, ...editable } = payload;
        await api.patch(`/companies/${companyId}/invoices/${invoiceId}`, editable);
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save invoice");
    } finally { setBusy(false); }
  }

  async function transition(target) {
    if (target === "void" && !window.confirm(`Void ${invoice.number}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/invoices/${invoiceId}/transition`,
                     { target_status: target });
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not transition");
    } finally { setBusy(false); }
  }

  const isDraft = invoice?.status === "draft" || isNew;
  const canIssue = invoice?.status === "draft";
  const canPay = invoice?.status === "issued";
  const canVoid = invoice?.status === "draft" || invoice?.status === "issued";

  return (
    <DetailDrawer open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <div className="p-4" data-testid="invoice-drawer">
        <div className="flex items-center justify-between mb-3">
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-brand-ink flex items-center gap-2">
              <span>{isNew ? "New invoice" : invoice?.number}</span>
              {invoice && (
                <span className={`chip mono-num text-[10px] ${STATUS_CHIP[invoice.status]}`}>
                  {invoice.status}
                </span>
              )}
            </div>
            {invoice?.source_card_id && (
              <div className="text-[10.5px] text-brand-muted mt-0.5">
                Generated from card {invoice.source_card_id.slice(0,8)} · {invoice.source_minutes_logged} mins logged
              </div>
            )}
          </div>
          <button className="btn-ghost h-7 w-7 !p-0" onClick={onClose}><X size={12}/></button>
        </div>

        {!isNew && invoice && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <a href={`/c/${companyId}/invoices/${invoice.id}/print`} target="_blank" rel="noreferrer"
               className="btn-ghost h-8 text-[11.5px]" data-testid="invoice-drawer-print">
              <Printer size={12}/> Print / Save PDF
            </a>
            {canManage && canIssue && (
              <button className="btn-outline h-8 text-[11.5px]" onClick={() => transition("issued")}
                      disabled={busy} data-testid="invoice-issue">
                Mark issued
              </button>
            )}
            {canManage && canPay && (
              <button className="btn-violet h-8 text-[11.5px]" onClick={() => transition("paid")}
                      disabled={busy} data-testid="invoice-mark-paid">
                <CheckCircle2 size={12}/> Mark paid
              </button>
            )}
            {canManage && canVoid && (
              <button className="btn-ghost h-8 text-[11.5px] text-coral"
                      onClick={() => transition("void")} disabled={busy}
                      data-testid="invoice-void">
                <XCircle size={12}/> Void
              </button>
            )}
          </div>
        )}

        {/* Editable form (draft) */}
        {isDraft && form && (
          <div className="space-y-3">
            <div>
              <label className="micro-label">Customer</label>
              <select value={form.customer_id}
                      onChange={(e) => setForm(f => ({...f, customer_id: e.target.value}))}
                      disabled={!canManage || !isNew}
                      className="field-input" data-testid="invoice-customer">
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="micro-label">Issue date</label>
                <input type="date" value={form.issue_date || ""}
                       onChange={(e) => setForm(f => ({...f, issue_date: e.target.value}))}
                       disabled={!canManage} className="field-input mono-num"
                       data-testid="invoice-issue-date"/>
              </div>
              <div>
                <label className="micro-label">Due date</label>
                <input type="date" value={form.due_date || ""}
                       onChange={(e) => setForm(f => ({...f, due_date: e.target.value}))}
                       disabled={!canManage} className="field-input mono-num"
                       data-testid="invoice-due-date"/>
              </div>
            </div>
            <div>
              <label className="micro-label">Line items</label>
              <div className="space-y-1 mt-1">
                {form.line_items.map((li, i) => (
                  <div key={i} className="flex items-center gap-1"
                       data-testid={`invoice-line-${i}`}>
                    <input value={li.description}
                           onChange={(e) => setLine(i, "description", e.target.value)}
                           placeholder="Description" disabled={!canManage}
                           className="field-input flex-1 !h-8 text-[12px]"/>
                    <input type="number" value={li.qty} step="0.01"
                           onChange={(e) => setLine(i, "qty", e.target.value)}
                           disabled={!canManage} className="field-input !w-16 !h-8 mono-num text-[12px]"/>
                    <input type="number" value={(li.unit_price_cents / 100).toFixed(2)}
                           step="0.01" placeholder="0.00"
                           onChange={(e) => setLine(i, "unit_price_cents", Math.round(Number(e.target.value) * 100))}
                           disabled={!canManage}
                           className="field-input !w-20 !h-8 mono-num text-[12px]"/>
                    <div className="mono-num text-[11px] text-brand-muted w-20 text-right">
                      {fmtCents(Math.round(Number(li.qty || 0) * Number(li.unit_price_cents || 0)))}
                    </div>
                    {canManage && form.line_items.length > 1 && (
                      <button className="btn-ghost h-7 w-7 !p-0"
                              onClick={() => delLine(i)}
                              data-testid={`invoice-line-del-${i}`}>
                        <Trash2 size={11}/>
                      </button>
                    )}
                  </div>
                ))}
                {canManage && (
                  <button className="btn-ghost h-7 text-[11px] mt-1" onClick={addLine}
                          data-testid="invoice-line-add">
                    <Plus size={11}/> Add line
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="micro-label">Notes</label>
              <textarea value={form.notes} rows={2}
                        onChange={(e) => setForm(f => ({...f, notes: e.target.value}))}
                        disabled={!canManage} className="field-input"
                        data-testid="invoice-notes"/>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-brand-rule">
              <div className="text-[12px] text-brand-muted">Total</div>
              <div className="mono-num text-[18px] font-bold text-brand-ink"
                   data-testid="invoice-form-total">{fmtCents(linesTotal)}</div>
            </div>
            {canManage && (
              <button className="btn-violet w-full" onClick={save} disabled={busy}
                      data-testid="invoice-save">
                {busy ? "…" : (isNew ? "Create draft" : "Save changes")}
              </button>
            )}
          </div>
        )}

        {/* Read-only view (issued / paid / void) */}
        {!isDraft && invoice && (
          <div className="space-y-3" data-testid="invoice-readonly">
            <div className="card-panel p-3 text-[12px]">
              <div className="micro-label">Bill to</div>
              <div className="mt-1 text-brand-ink font-medium">{invoice.customer_snapshot?.name}</div>
              <div className="text-brand-muted whitespace-pre-line">
                {invoice.customer_snapshot?.email}{invoice.customer_snapshot?.address ? "\n" : ""}
                {invoice.customer_snapshot?.address}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[12px]">
              <div className="card-panel p-2">
                <div className="micro-label">Issue</div>
                <div className="mono-num">{invoice.issue_date}</div>
              </div>
              <div className="card-panel p-2">
                <div className="micro-label">Due</div>
                <div className="mono-num">{invoice.due_date}</div>
              </div>
            </div>
            <div className="card-panel overflow-hidden">
              <table className="fable-table !text-[11.5px]">
                <thead>
                  <tr><th>Description</th><th className="text-right">Qty</th>
                      <th className="text-right">Unit</th><th className="text-right">Amount</th></tr>
                </thead>
                <tbody>
                  {invoice.line_items.map((li) => (
                    <tr key={li.id}>
                      <td>{li.description}</td>
                      <td className="text-right mono-num">{li.qty}</td>
                      <td className="text-right mono-num">{fmtCents(li.unit_price_cents)}</td>
                      <td className="text-right mono-num">{fmtCents(li.subtotal_cents)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-brand-ink font-bold">
                    <td colSpan={3} className="text-right">Total</td>
                    <td className="text-right mono-num" data-testid="invoice-total">
                      {fmtCents(invoice.total_cents)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {invoice.paid_at && (
              <div className="text-[11.5px] text-emerald-800 mono-num">Paid {invoice.paid_at}</div>
            )}
            {invoice.notes && (
              <div className="card-panel p-3 text-[11.5px] text-brand-muted whitespace-pre-wrap">
                {invoice.notes}
              </div>
            )}
          </div>
        )}
      </div>
    </DetailDrawer>
  );
}
